import { useCallback, useMemo } from "react"
import { useTimeLineGraphOption } from "./options"
import { BarData, useTimeLineGraphModel } from "./model"
import { numericValueFormat } from "../../common"
import { useStackLegend } from "../../../components/stackLegend"
import { useTooltip } from "../../../components/toolTip"
import { useGrid } from "../../../components/grid"
import { textStyle } from "../../../../../constants"

type useTimeLineGraphRenderOptionProps<T> = {
	graphOption: ReturnType<typeof useTimeLineGraphOption<T>>
	graphData: ReturnType<typeof useTimeLineGraphModel<T>>
}
export const useTimeLineGraphRenderOption = <T,>({
	graphOption: {
		configure, valueDef, stackDef, summary
	},
	graphData
}: useTimeLineGraphRenderOptionProps<T>) => {
	const grid = useGrid({ topMargin: true, containLabel: true })
	const tooltip = useTooltip({ trigger: 'axis', valueDef })
  const legend = useStackLegend({ show: (configure.stack || stackDef) ? true : false })

	const valueFormatter = useCallback(
    (value: number | string) => {
      if (value === undefined || value === null) return ''
			return numericValueFormat(value, 1, valueDef?.unit)
		},
    [valueDef]
  )
	const { xAxis, yAxis, series } = useMemo(
		() => {
			if (!graphData) return {}

			const {
				operation, bars, seriesList,
				minTime, maxTime
			} = graphData

			const xAxis: {
				type: 'category' | 'time'
				axisTick?: { show: boolean }
				axisLabel?: {
					align: 'center',
					interval: number
				}
				min?: Date
				max?: Date
				data?: string[]
				show?: boolean
			}[] = [{
        type: 'category',
        axisTick: { show: false },
        axisLabel: {
          align: 'center',
          interval: 0
        },
        data: bars.map((time) => operation.format(time) ?? '')
			}]
			if (summary) {
				xAxis.push({
					type: 'time',
					min: minTime,
					max: maxTime,
					show: false
				})
			}
			const yAxis = [{
        type: 'value',
        axisLabel: {
          formatter: valueFormatter
        }
			}]
			const series: {
				id: string
				name: string
				type: string,
				symbole?: 'none' | 'circle'
				symboleSize?: number
				stack?: string
				color?: string
				barWidth?: number
				barGap?: string
				smooth?: boolean
				xAxisIndex: number
				yAxisIndex: number
				emphasis?: { focus: string },
				data: (BarData | (Date | number)[])[]
			}[] = seriesList.map((series, i) => ({
				id: series.code + "-bar",
				name: series.name ?? `series-${i}`,
				type: 'bar',
				symbol: 'none',
				stack: series.code === 'all' ? 'total' : 'premitive',
				color: series.code === 'all' ? 'gray' : undefined,
				barWidth: series.code === 'all' ? 10 : undefined,
				barGap: "5%",
				xAxisIndex: 0,
				yAxisIndex: 0,
				data: series.bar
			}))
			if (summary) {
				series.push(...seriesList.map((series, i) => ({
					id: series.code + "-line",
					name: (series.name ?? `series-${i}`) + '(累計)',
					type: 'line',
					smooth: true,
					symbolSize: series.code === 'all' ? undefined : 5,
					symbol: series.code === 'all' ? undefined : 'circle',
					color: series.code === 'all' ? 'gray' : undefined,
					xAxisIndex: 1,
					yAxisIndex: 0,
					emphasis: { focus: 'series' },
					data: series.timeLine.map((data) => [data.time, data.total])
				})))
			}
			return { xAxis, yAxis, series }
		},
		[graphData, valueFormatter, summary]
	)
	return {
		//animation: false,
		tooltip,
		legend,
		grid,
		xAxis,
		yAxis,
		series,
		textStyle
	}
}